import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormGroupDirective, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BusinessService } from '@app/business/business.service';
import { SessionStorageService } from '@app/core';
import { InfoService } from '@app/core/info/info.service';
import { getWebIdentificationStatus, WebIdentificationStatusResult } from '@app/shared/helpers/web-identification-status.helper';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cloud-web-dentification',
  templateUrl: './web-dentification.component.html',
  styleUrls: ['./web-dentification.component.scss']
})
export class WebDentificationComponent {
  @ViewChild('identificatorFormRef') identificatorFormRef!: FormGroupDirective;
  identificatorForm: FormGroup;
  isSubmitting: boolean = false;
  businessAccountId: any;
  hasSecondTabBeenSelected = false;
  showBrowser: boolean = true;
  showMobile: boolean = false;
  displayedColumns: string[] = ['status','createdAt', 'email', 'phoneNumber',  'actions'];
  idintificatorDataSource: any;
  isLoading = true;
  offset: number = 0;
  limit: number = 10;
  
  constructor(
    private formBuilder: FormBuilder,
    private businessService: BusinessService,
    public info: InfoService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private translateService: TranslateService,
  ) {
    this.identificatorForm = this.formBuilder.group({
      email: ['', [Validators.email, Validators.required]],
      phone: ['', [Validators.required, Validators.pattern(/^\+\d{6,15}$/)]],
    });
  }

  ngOnInit(): void {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
  } 

  onSubmitIdentificatorForm() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.businessService.requestWEbIdentification(this.identificatorForm.value, this.businessAccountId)
      .subscribe((res: any) => {
        if (res.status !== 200) {
          this.info.notice(res.responseCode)
          if (!res.responseCode) {
            this.info.warning(this.translateService.instant('businessTexts.requestWebIdentificationwarning'))
          }
        } else {
          this.info.success(this.translateService.instant('businessTexts.requestWebIdentificationSent'))
        }
        this.identificatorFormRef.resetForm({ email: '', phone: '' });
        this.isSubmitting = false;
        this.loadWebIdentificator()
      })
  }

  isItDanger(row: any): WebIdentificationStatusResult {
    const statusSesionTranslate = this.translateService.instant('businessTexts.statusSession')
    return getWebIdentificationStatus(row, statusSesionTranslate);
  }

  loadWebIdentificator() {
    const formValues = { ...this.identificatorForm.value };
    const searchCriteria = {
      ...formValues,
      offset: this.offset,
      limit: this.limit
    };
    this.businessService.getRequestsWebIdentification(this.businessAccountId, searchCriteria).subscribe((response: any) => {
      this.idintificatorDataSource = new MatTableDataSource(response.body?.data);
    })
      this.isLoading = false;
  }

  hasData(): boolean {
    return (this.idintificatorDataSource && this.idintificatorDataSource.data.length !== 0);
  }

  viewUser(row: any) {
    this.businessService.setIdentificatorData(row);
    this.router.navigate(['business/signingotc'], {
      queryParams: { id: row.id },
      state: { row }
    });
  }

  downloadUEIJSON(row: any) {
    const rowId = row.id;
    const idResult = row.result.id;
    const signsessionId = row.result.signsessionId
    const companyCmrId = this.sessionStorageService.getItem("businessAccountId");
    const noDataDownloadMsg = this.translateService.instant('businessTexts.noDataDownload');

    this.businessService.getDocumentList(companyCmrId, rowId)
      .subscribe((response: any) => {
        const interestedDocument = response?.body?.data?.find((doc: any) => doc.resultId === idResult);
        const nameFile = interestedDocument?.fileName;
        if (!nameFile) {
          this.info.warning(noDataDownloadMsg);
          return;
        }
        
        this.businessService.getDownloadIdentification(companyCmrId, idResult, signsessionId)
        .subscribe((identificationResponse: any) => {
            const data = identificationResponse?.body?.data;
            if (!data) {
              this.info.warning(noDataDownloadMsg);
              return;
            }
            const jsonStr = JSON.stringify(data, null, 2);
            const jsonFileName = nameFile.replace(/\.[^/.]+$/, '');
            this.downloadBlobAsFile(jsonStr, jsonFileName, false, 'json');
          })
      })
  }

  downloadUEISIGN(row: any) {
    const signsessionId = row.result.signsessionId
    const idResult = row.result.id
    this.businessService.getDownloadUEISIGN(this.businessAccountId, idResult, signsessionId)
      .subscribe((response: any) => {
        const base64Data = response?.body?.data?.eIDDocAsBase64 || response?.data?.eIDDocAsBase64;
        if (!base64Data) {
          console.error('No base64 data found in response');
          return;
        }

        const byteCharacters = window.atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        this.downloadBlobAsFile(byteArray, row.name.en, true, "pdf");
      });
  }

  downloadUEICREATE(row: any) {
    const idResult = row.result.id
    const websessionId = row.result.webssionId

    this.businessService.getDownloadUEICREATE(this.businessAccountId, idResult, websessionId)
      .subscribe((response: any) => {
        const base64Data = response.body.data.eIDDocAsBase64;
        const byteCharacters = window.atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        this.downloadBlobAsFile(byteArray, row.name.en, false, "pdf");
      });
  }

  private downloadBlobAsFile(byteArray: any, nameFile: any = 'document', isBlank: boolean, type: "pdf" | "json") {
    const blob = new Blob([byteArray], { type: `application/${type}` });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nameFile+`.${type}`;
    if (isBlank) {
      a.target = '_blank'; // само за тест
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Helper method to determine the current locale for date formatting
  get currentLocale(): string {
    return this.translateService.currentLang === 'en' ? 'en-US' : 'bg-BG';
  }
}